import { NavLink, Outlet, Link } from "react-router-dom";
import { LayoutDashboard, Home, BedDouble, BookOpen, Images, Inbox, Settings, LogOut, ExternalLink, Newspaper, GalleryHorizontal, BarChart3, MapPinned, Tent } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/site/Seo";

const LINKS = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true, id: "overview" },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, id: "analytics" },
  { to: "/admin/slides", label: "Homepage Slider", icon: GalleryHorizontal, id: "slides" },
  { to: "/admin/retreats", label: "Retreats", icon: Tent, id: "retreats" },
  { to: "/admin/places", label: "Area & Activity Pages", icon: MapPinned, id: "places" },
  { to: "/admin/listings?kind=sale", label: "Homes & Land", icon: Home, id: "sale" },
  { to: "/admin/listings?kind=rent", label: "Places to Stay", icon: BedDouble, id: "rent" },
  { to: "/admin/posts", label: "Stories / AI Blog", icon: Newspaper, id: "posts" },
  { to: "/admin/directory", label: "Directory", icon: BookOpen, id: "directory" },
  { to: "/admin/gallery", label: "Gallery", icon: Images, id: "gallery" },
  { to: "/admin/inquiries", label: "Inquiries", icon: Inbox, id: "inquiries" },
  { to: "/admin/settings", label: "Site Settings", icon: Settings, id: "settings" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen bg-linen lg:flex">
      <Seo title="Dashboard | Escamequita" description="Owner dashboard" noindex />
      <aside className="border-b border-clay bg-white lg:fixed lg:inset-y-0 lg:w-64 lg:border-b-0 lg:border-r" data-testid="admin-sidebar">
        <div className="p-6">
          <Link to="/" className="font-logo text-2xl font-black text-forest">Escamequita</Link>
          <p className="mt-1 text-xs font-bold uppercase tracking-[0.2em] text-sage">Owner dashboard</p>
        </div>
        <nav className="flex gap-1 overflow-x-auto px-3 pb-3 lg:flex-col lg:pb-0">
          {LINKS.map(({ to, label, icon: Icon, end, id }) => (
            <NavLink key={id} to={to} end={end} data-testid={`admin-nav-${id}`}
              className={({ isActive }) => `flex shrink-0 items-center gap-3 rounded-lg px-4 py-2.5 text-sm font-bold transition-colors duration-200 ${isActive && (!to.includes("?") || window.location.search === to.slice(to.indexOf("?"))) ? "bg-forest text-white" : "text-ink/75 hover:bg-linen"}`}>
              <Icon className="h-4 w-4" /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden space-y-1 border-t border-clay p-3 lg:absolute lg:bottom-0 lg:block lg:w-full">
          <p className="truncate px-4 py-2 text-xs font-semibold text-moss">{user?.email}</p>
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-lg px-4 py-2.5 text-sm font-bold text-ink/75 hover:bg-linen" data-testid="admin-view-site"><ExternalLink className="h-4 w-4" /> View site</a>
          <button onClick={logout} className="flex w-full items-center gap-3 rounded-lg px-4 py-2.5 text-sm font-bold text-ink/75 hover:bg-linen" data-testid="admin-logout"><LogOut className="h-4 w-4" /> Log out</button>
        </div>
        <button onClick={logout} className="mx-3 mb-3 rounded-lg px-4 py-2 text-sm font-bold text-forest lg:hidden" data-testid="admin-logout-mobile">Log out</button>
      </aside>
      <main className="flex-1 p-5 sm:p-8 lg:ml-64 lg:p-12"><Outlet /></main>
    </div>
  );
}

export const AdminHeader = ({ title, children }) => (
  <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
    <h1 className="text-3xl font-black text-forest sm:text-4xl" data-testid="admin-page-title">{title}</h1>
    <div className="flex flex-wrap gap-3">{children}</div>
  </div>
);
